/*
 * Copyright Flashy All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import {
  ComponentType,
  GlobalPositionStrategy,
  Overlay,
  OverlayConfig,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector, StaticProvider } from '@angular/core';
import { createNoopInjectionToken } from 'ngxtension/create-injection-token';

import { FwyModalRef } from './FwyModalRef';

export const [injectModalData, provideModalData] =
  createNoopInjectionToken<unknown>('Flashy UI modal data');

export interface IFlashyModalConfig<T = unknown>
  extends Omit<OverlayConfig, 'positionStrategy'> {
  data?: T;
  disableClose?: boolean;
  disableAnimation?: boolean;
}

@Injectable()
export class FwyModal {
  static createConfig<T>(
    config?: IFlashyModalConfig<T>
  ): IFlashyModalConfig<T> {
    return {
      disableClose: false,
      hasBackdrop: true,
      disposeOnNavigation: true,
      disableAnimation: false,
      ...config,
    };
  }

  constructor(
    private overlay: Overlay,
    private injector: Injector
  ) {}

  open<T, R = unknown>(
    cmp: ComponentType<unknown>,
    config?: IFlashyModalConfig<T>,
    providers?: StaticProvider[],
    injector = this.injector
  ): FwyModalRef<T, R> {
    const configOptions = FwyModal.createConfig(config);
    const overlay = this.overlay.create(configOptions);
    const position = new GlobalPositionStrategy();
    if (!configOptions.disableAnimation) {
      overlay.addPanelClass(['flashy-modal', 'fade']);
    }
    position.centerVertically();
    position.centerHorizontally();
    overlay.updatePositionStrategy(position);
    const modalRef = new FwyModalRef<T, R>(overlay, configOptions);
    const cmpPortal = new ComponentPortal(
      cmp,
      null,
      Injector.create({
        providers: [
          provideModalData(config?.data),
          { provide: FwyModalRef, useValue: modalRef },
          ...(providers || []),
        ],
        parent: injector,
      })
    );
    overlay.attach(cmpPortal);
    setTimeout(() => {
      if (!configOptions.disableAnimation) {
        overlay.addPanelClass('show');
      }
      // position.centerVertically();
      // overlay.updatePositionStrategy(position);
    }, 100);
    return modalRef;
  }
}
